import React, { useState } from "react";

import { GoogleMap, InfoWindow } from "@react-google-maps/api";
import { colors } from "@zapalacorp/tokens";
import { Feature } from "geojson";
import * as I18nIsoCountries from "i18n-iso-countries";
import * as topojson from "topojson-client";
import { GeometryCollection, Topology } from "topojson-specification";

import world from "../../../../countries.json";
import { IDashboardData } from "../../../../models/overview/dashboard";
import NotEnoughData from "../../../DashMetrics/NotEnoughData";
import useStyles from "./styles";

I18nIsoCountries.registerLocale(require("i18n-iso-countries/langs/en.json"));

const CENTER_MAP = { lat: 0, lng: 0 };
const ZOOM_MAP = 3;

type LatLng = {
  lat: number;
  lng: number;
};

type MapInformation = {
  name: string;
  position: LatLng;
  value: number;
};

interface Props {
  datasets: IDashboardData[];
}

const OverviewMapGraph = ({ datasets }: Props) => {
  const classes = useStyles();
  const [information, setInformation] = useState<MapInformation>();

  if (!datasets[0].data.length) return <NotEnoughData />;

  const countryFeatures = topojson.feature(
    world as unknown as Topology,
    world.objects.countries as GeometryCollection
  ).features;

  const maxValue = datasets.reduce((maxDatasetValue: number, { data }: any) => {
    const maxDataValue = data.reduce((maxValue: number, { value }: any) => {
      return Math.max(maxValue, value);
    }, -1);

    return Math.max(maxDatasetValue, maxDataValue);
  }, -1);

  const getColorByValue = (value: number) => {
    const normalizedValue = value / maxValue;

    if (normalizedValue > 2 / 3) return colors.error600;
    if (normalizedValue > 1 / 3) return colors.warning600;
    if (normalizedValue > 0) return colors.success600;

    return "#D0D5DD";
  };

  const getFilteredFeatureCollection = () => {
    const features: Feature[] = [];

    datasets.forEach(({ data }: any) => {
      data.forEach(({ code, value }: any) => {
        if (code) {
          const country = countryFeatures.find(
            ({ properties }: any) => I18nIsoCountries.getAlpha2Code(properties?.name, "en") === code
          );

          if (country) {
            country.properties = { ...country.properties, value };
            features.push(country);
          }
        }
      });
    });

    return { type: "FeatureCollection", features };
  };

  const handleLoadMap = (map: google.maps.Map) => {
    const countryLayer = new window.google.maps.Data();

    // Set and apply styling to the countryLayer
    countryLayer.setStyle((feature: any) => {
      const name = feature.getProperty("name");
      const value = feature.getProperty("value");

      return {
        fillColor: getColorByValue(value), // call function to get color for country based on the value
        fillOpacity: 0.5,
        strokeColor: "#FFFFFF",
        strokeWeight: 1,
        zIndex: 1,
        title: name,
        visible: true,
        clickable: true,
      };
    });

    countryLayer.addListener("click", (e: any) => {
      const name = e.feature.getProperty("name");
      const value = e.feature.getProperty("value");
      const position = { lat: e.latLng.lat(), lng: e.latLng.lng() };

      setInformation({ name, position, value });
    });

    countryLayer.addGeoJson(getFilteredFeatureCollection(), { idPropertyName: "id" });
    countryLayer.setMap(map);
  };

  return (
    <GoogleMap
      center={CENTER_MAP}
      mapContainerClassName={classes.mapContainer}
      onLoad={handleLoadMap}
      options={{
        fullscreenControl: false,
        mapTypeControl: false,
        mapTypeId: "roadmap",
        streetViewControl: false,
        styles: [
          {
            featureType: "administrative",
            elementType: "all",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "administrative.country",
            elementType: "geometry.stroke",
            stylers: [{ color: "#FFFFFF" }, { visibility: "on" }],
          },
          {
            featureType: "administrative.country",
            elementType: "labels.text.fill",
            stylers: [{ visibility: "on" }, { color: "#667085" }],
          },
          {
            elementType: "labels.text.stroke",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "landscape",
            elementType: "geometry",
            stylers: [{ color: "#D0D5DD" }],
          },
          {
            featureType: "poi",
            elementType: "all",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "road",
            elementType: "all",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#F1F6FB" }],
          },
          {
            featureType: "water",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      }}
      zoom={ZOOM_MAP}
    >
      {information && (
        <InfoWindow position={information.position} onCloseClick={() => setInformation(undefined)}>
          <div>
            {information.name}: {information.value}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default OverviewMapGraph;
